import type { codec } from "@irriga-mais/proto";
import type { DirectusRelationFieldType } from "@/utils/types";
import type { Device } from "./device";
export type CurrentLICPacketRelationsTypes = {
  device: DirectusRelationFieldType<Device>;
};

export type CurrentLICPacketDefaultRelationsTypes = {
  device: DirectusRelationFieldType<Device>;
};

type OutgoingPacketPayloadKeys = Exclude<keyof codec.out.IOutgoingPacket, "id">;

export const LIC_PACKET_PAYLOAD_TYPES = [
  "status",
  "info",
  "scheduling_report",
  "automation_report",
  "ack",
  "raw",
] as const satisfies ReadonlyArray<OutgoingPacketPayloadKeys>;

// Compile‐time check that we didn’t forget any key:
type _MissingPacketKeys = Exclude<OutgoingPacketPayloadKeys, (typeof LIC_PACKET_PAYLOAD_TYPES)[number]>;

type _CheckLICPacketPayloadTypes = _MissingPacketKeys extends never
  ? true
  : ["Missing packet payload types:", _MissingPacketKeys];

const _checkLICPacketPayloadTypes: _CheckLICPacketPayloadTypes = true;

export type LICPacketPayloadType = (typeof LIC_PACKET_PAYLOAD_TYPES)[number];

/**
 * A single‐key payload with an `id`.
 * The key is guaranteed to be one of
 * the OutgoingPacketPayloadKeys, and its value
 * is the corresponding NonNullable<>.
 */
export type LICPacketPayloadData<P extends LICPacketPayloadType> = P extends LICPacketPayloadType
  ? { id: NonNullable<codec.out.IOutgoingPacket["id"]> } & Record<P, NonNullable<codec.out.IOutgoingPacket[P]>>
  : never;

export function isStatusPacketPayload(
  payload: LICPacketPayloadData<LICPacketPayloadType>
): payload is LICPacketPayloadData<"status"> {
  return (payload as LICPacketPayloadData<"status">).status !== undefined;
}

export function isInfoPacketPayload(
  payload: LICPacketPayloadData<LICPacketPayloadType>
): payload is LICPacketPayloadData<"info"> {
  return (payload as LICPacketPayloadData<"info">).info !== undefined;
}

export function isSchedulingReportPacketPayload(
  payload: LICPacketPayloadData<LICPacketPayloadType>
): payload is LICPacketPayloadData<"scheduling_report"> {
  return (payload as LICPacketPayloadData<"scheduling_report">).scheduling_report !== undefined;
}

export function isAutomationReportPacketPayload(
  payload: LICPacketPayloadData<LICPacketPayloadType>
): payload is LICPacketPayloadData<"automation_report"> {
  return (payload as LICPacketPayloadData<"automation_report">).automation_report !== undefined;
}

export function isAckPacketPayload(
  payload: LICPacketPayloadData<LICPacketPayloadType>
): payload is LICPacketPayloadData<"ack"> {
  return (payload as LICPacketPayloadData<"ack">).ack !== undefined;
}

export function isRawPacketPayload(
  payload: LICPacketPayloadData<LICPacketPayloadType>
): payload is LICPacketPayloadData<"raw"> {
  return (payload as LICPacketPayloadData<"raw">).raw !== undefined;
}

export interface CurrentLICPacket<
  PayloadType extends LICPacketPayloadType = LICPacketPayloadType,
  Types extends Partial<CurrentLICPacketRelationsTypes> = CurrentLICPacketDefaultRelationsTypes,
> {
  id: number;
  date_created: string;
  device: string | Types["device"];
  packet_date: string;
  payload_type: PayloadType;
  payload_data: LICPacketPayloadData<PayloadType>;
}
